import { useState } from 'react';
import type { ExpenseCategory, SolTemporalKpiSnapshot } from '@economi/domain-contracts';
import {
  formatSolesFromCents,
  simulateExpenseImpact,
} from '../../services/solImpactEngine';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import styles from './SolImpactSimulator.module.css';

interface SolImpactSimulatorProps {
  readonly snapshot: SolTemporalKpiSnapshot;
  readonly onQuickRegisterSimulatedExpense: (params: {
    amountSoles: number;
    merchantName: string;
    category: ExpenseCategory;
    description: string;
  }) => void;
}

const SIMULATOR_PRESETS = [
  { label: 'S/ 18 Menú', soles: 18, merchant: 'Menú Ejecutivo Miraflores', category: 'FOOD_DINING' as const },
  { label: 'S/ 45 Taxi', soles: 45, merchant: 'Uber / Cabify Corporativo', category: 'TRANSPORT' as const },
  { label: 'S/ 120 Compras', soles: 120, merchant: 'Supermercado Tottus', category: 'GROCERIES' as const },
  { label: 'S/ 280 Salida', soles: 280, merchant: 'Salida Fin de Semana', category: 'ENTERTAINMENT' as const },
  { label: 'S/ 650 Tech', soles: 650, merchant: 'Tienda Tecnología / Gadget', category: 'SHOPPING' as const },
];

export function SolImpactSimulator({
  snapshot,
  onQuickRegisterSimulatedExpense,
}: SolImpactSimulatorProps) {
  const [proposedSoles, setProposedSoles] = useState<number>(120);

  const simulation = simulateExpenseImpact(snapshot, proposedSoles);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
          <h3 className={styles.title}>
            Simulador: ¿Si gasto S/ {proposedSoles.toFixed(0)} hoy?
          </h3>
          <InfoPopover title="Simulador de Impacto en Vivo">
            <span>
              Calcula cómo cambiaría tu Cuota Diaria Segura en los {snapshot.remainingDays} días restantes antes de realizar la compra.
            </span>
            <span>{simulation.narrativeSummary}</span>
          </InfoPopover>
        </div>
        <span className={styles.liveBadge}>EN VIVO</span>
      </div>

      <div className={styles.controlsRow}>
        <div className={styles.amountDisplayRow}>
          <label htmlFor="sim-soles-input" className={styles.amountLabel}>
            Monto a simular:
          </label>
          <div className={styles.amountInputWrap}>
            <span className={styles.currencyPrefix}>S/</span>
            <input
              id="sim-soles-input"
              type="number"
              min={1}
              max={5000}
              step={5}
              value={proposedSoles}
              onChange={(e) =>
                setProposedSoles(Math.max(0, Number(e.target.value) || 0))
              }
              className={styles.amountInput}
            />
          </div>
        </div>

        <input
          type="range"
          min={5}
          max={1500}
          step={5}
          value={Math.min(1500, proposedSoles)}
          onChange={(e) => setProposedSoles(Number(e.target.value))}
          className={styles.slider}
          aria-label="Deslizador de monto en Soles a simular"
        />

        <div className={styles.presetRow}>
          {SIMULATOR_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              className={`${styles.presetBtn} ${
                proposedSoles === preset.soles ? styles.presetBtnActive : ''
              }`}
              onClick={() => setProposedSoles(preset.soles)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.impactGrid}>
        <div className={styles.impactBox}>
          <span className={styles.impactLabel}>Nueva Cuota Diaria</span>
          <span
            className={`${styles.impactValue} ${
              simulation.willExceedCycleCap
                ? styles.impactValueDanger
                : styles.impactValuePositive
            }`}
          >
            {formatSolesFromCents(simulation.newSafeDailyAllowanceCents)}/día
          </span>
          <span className={styles.impactLabel}>
            Actual: {formatSolesFromCents(simulation.currentSafeDailyAllowanceCents)}/día
          </span>
        </div>

        <div className={styles.impactBox}>
          <span className={styles.impactLabel}>Recorte Diario (ΔD)</span>
          <span className={`${styles.impactValue} ${styles.impactValueWarning}`}>
            -{formatSolesFromCents(simulation.dailyAllowanceReductionCents)}/día
          </span>
          <span className={styles.impactLabel}>
            Por {snapshot.remainingDays} días
          </span>
        </div>

        <div className={styles.impactBox}>
          <span className={styles.impactLabel}>Tiempo de Ciclo</span>
          <span
            className={`${styles.impactValue} ${
              simulation.budgetTimeCostDays >= 2
                ? styles.impactValueDanger
                : styles.impactValueWarning
            }`}
          >
            {simulation.budgetTimeCostHours} hrs
          </span>
          <span className={styles.impactLabel}>
            {simulation.budgetTimeCostDays} días de tope
          </span>
        </div>
      </div>

      <div className={styles.actionRow}>
        <button
          type="button"
          className={styles.commitBtn}
          onClick={() => {
            if (proposedSoles <= 0) return;
            const matchedPreset = SIMULATOR_PRESETS.find((p) => p.soles === proposedSoles);
            onQuickRegisterSimulatedExpense({
              amountSoles: proposedSoles,
              merchantName: matchedPreset?.merchant ?? 'Gasto Evaluado en Simulador',
              category: matchedPreset?.category ?? 'OTHER',
              description: `Registrado desde Simulador (-${formatSolesFromCents(simulation.dailyAllowanceReductionCents)}/día)`,
            });
          }}
        >
          + Registrar {formatSolesFromCents(simulation.proposedExpenseCents)} Ahora
        </button>
      </div>
    </div>
  );
}
